import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useRef, useState } from "react"

import { useGetInventoryShelvingOptions } from "../../../../hooks"
import { useGetTransferProductInventories } from "../../hooks/useGetTransferProductInventories"

import { TransferProductSelectionChips } from "./TransferProductSelectionChips"
import { TransferProductSelectionTable } from "./TransferProductSelectionTable"

import { Button } from "@/components/common/Button"
import CancelDialog from "@/components/common/Modals/CancelDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import SearchInput from "@/components/SearchInput/SearchInput"
import { AerosMultiSelect } from "@/components/UiKit/AerosMultiSelect"
import type { InventoryByProductItem } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"

interface TransferProductSelectionDialogProps {
  warehouseAreaId: string
  addedInventories: InventoryByProductItem[]
  onClose: () => void
  onSubmit: (inventories: InventoryByProductItem[]) => void
}

export const TransferProductSelectionDialog: FC<TransferProductSelectionDialogProps> = ({
  warehouseAreaId,
  addedInventories,
  onClose,
  onSubmit,
}) => {
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedAisleCodes, setSelectedAisleCodes] = useState<string[]>([])
  const [selectedInventories, setSelectedInventories] = useState<InventoryByProductItem[]>([])
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const { inventories, isFetching, loadMore } = useGetTransferProductInventories({
    warehouseAreaId,
    query: searchQuery,
    aisleCodes: selectedAisleCodes,
  })
  const { aisleOptions } = useGetInventoryShelvingOptions({ warehouseAreaId, includeUnavailable: true })

  const tableContainerRef = useRef<HTMLDivElement>(null)
  const handleScroll = useScrollLoadMore({ fetching: isFetching, loadMore, scrollContainerRef: tableContainerRef })

  const canSubmit = selectedInventories.length > 0

  const handleSelectedInventoryRemove = (selectedInventoryToRemove: InventoryByProductItem) => {
    setSelectedInventories((prev) =>
      prev.filter(
        (selectedInventory) => selectedInventory.product.productId !== selectedInventoryToRemove.product.productId
      )
    )
  }

  const handleCancel = () => {
    if (canSubmit) {
      setIsCancelConfirmationOpen(true)
    } else {
      onClose()
    }
  }

  const handleSubmit = () => {
    onSubmit(selectedInventories)
  }

  if (isCancelConfirmationOpen) {
    return <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
  }

  return (
    <Dialog open onClose={handleCancel} fullWidth maxWidth='lg' data-testid='transfer.product-selection-dialog'>
      <DialogHeader title='Add Products' onClose={handleCancel} />
      <DialogContent className='flex flex-col gap-6 overflow-hidden'>
        <div className='flex items-end gap-6'>
          <SearchInput
            placeholder='Search Product ID, or Description'
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className='flex-1'
            data-testid='transfer.product-selection-dialog.search-field'
          />
          <AerosMultiSelect
            label='Filter by Aisle'
            placeholder='Select one'
            options={aisleOptions}
            value={selectedAisleCodes}
            onChange={setSelectedAisleCodes}
            withSelectAll
            className='flex-1'
            data-testid='transfer.product-selection-dialog.aisle-field'
          />
        </div>
        <TransferProductSelectionChips inventories={selectedInventories} onRemove={handleSelectedInventoryRemove} />
        <div className='flex min-h-0 flex-1'>
          <TransferProductSelectionTable
            data={inventories}
            addedInventories={addedInventories}
            selectedInventories={selectedInventories}
            loading={isFetching}
            containerRef={tableContainerRef}
            onSelectionChange={setSelectedInventories}
            onScroll={handleScroll}
          />
        </div>
      </DialogContent>
      <DialogActions className='justify-between px-6 pb-6'>
        <Button
          appearance='outlined'
          onClick={handleCancel}
          data-testid='transfer.product-selection-dialog.cancel-button'
        >
          Cancel
        </Button>
        <Button
          appearance='contained'
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid='transfer.product-selection-dialog.submit-button'
        >
          Add Products
        </Button>
      </DialogActions>
    </Dialog>
  )
}
